import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { logToDatadog } from '@/domains/shared/analytics/datadogUtils';
import { withTimeout } from '@/domains/shared/utils/withTimeout';
import { isNative } from '@/domains/shared/utils/native';

const BIOMETRIC_ENABLED_KEY = 'BIOMETRIC_ENABLED';
const LOG_PREFIX = '[BIO_MIGRATION]';

export const isBiometricAvailable = async (): Promise<boolean> => {
  if (!isNative()) {
    return false;
  }

  try {
    const result = await NativeBiometric.isAvailable();

    if (!result.isAvailable) {
      logToDatadog('info', `${LOG_PREFIX} isBiometricAvailable: false`, {
        biometryType: result.biometryType,
      });
      return false;
    }
    logToDatadog('info', `${LOG_PREFIX} isBiometricAvailable: true`, {
      biometryType: result.biometryType,
    });
    return true;
  } catch (error) {
    console.warn('Error checking biometric availability', error);
    logToDatadog('warn', `${LOG_PREFIX} isBiometricAvailable threw`, {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

export const isBiometricEnabled = async (): Promise<boolean> => {
  if (!isNative()) {
    return false;
  }

  try {
    const { value } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
    // Log the raw value alongside the boolean we return — if Datadog ever
    // shows `rawValue: 'true'` while the UI is treating biometrics as
    // disabled, the bug is downstream of this read (i.e. cached state),
    // not in storage.
    logToDatadog('info', `${LOG_PREFIX} isBiometricEnabled read`, {
      rawValue: value,
      result: value === 'true',
    });
    return value === 'true';
  } catch (error) {
    console.error('Error reading biometric preference', error);
    logToDatadog('error', `${LOG_PREFIX} isBiometricEnabled threw`, {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

export const setBiometricEnabled = async (enabled: boolean): Promise<void> => {
  if (!isNative()) {
    return;
  }
  try {
    await Preferences.set({
      key: BIOMETRIC_ENABLED_KEY,
      value: enabled ? 'true' : 'false',
    });
    logToDatadog('info', `${LOG_PREFIX} setBiometricEnabled wrote`, {
      enabled,
    });
  } catch (error) {
    console.error('Error saving biometric preference', error);
    logToDatadog('error', `${LOG_PREFIX} setBiometricEnabled threw`, {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

// If `verifyIdentity` never resolves or rejects (observed intermittently on
// cold boot where the native side appears to silently drop the prompt), the
// awaiting caller hangs forever. Capping the wait at 30s lets PinManager fall
// back to PIN entry instead of leaving the user on a white screen until they
// kill the app.
const VERIFY_IDENTITY_TIMEOUT_MS = 30_000;

export const promptBiometricAuth = async (): Promise<boolean> => {
  if (!isNative()) {
    return false;
  }

  logToDatadog('info', `${LOG_PREFIX} promptBiometricAuth called`);
  const start = Date.now();

  try {
    const outcome = await withTimeout(
      NativeBiometric.verifyIdentity({
        reason: 'Unlock your PensionBee app',
        title: 'Unlock with biometrics',
        subtitle: 'Confirm your identity',
        description: 'Use Face ID, Touch ID or your device biometrics',
      }),
      VERIFY_IDENTITY_TIMEOUT_MS,
      'NativeBiometric.verifyIdentity',
    );

    if (outcome === 'timeout') {
      logToDatadog(
        'error',
        `${LOG_PREFIX} promptBiometricAuth timed out after ${VERIFY_IDENTITY_TIMEOUT_MS}ms — native prompt never resolved`,
        { durationMs: Date.now() - start },
      );
      return false;
    }

    logToDatadog('info', `${LOG_PREFIX} promptBiometricAuth succeeded`, {
      durationMs: Date.now() - start,
    });
    return true;
  } catch (error) {
    logToDatadog(
      'warn',
      `${LOG_PREFIX} promptBiometricAuth failed or was cancelled`,
      {
        durationMs: Date.now() - start,
        error: error instanceof Error ? error.message : String(error),
      },
    );
    console.warn('Biometric authentication failed or was cancelled', error);
    return false;
  }
};
